import { useMemo } from 'react';
import { TagType } from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { Contact } from '@sib/ui/icons';
import {
  createCategoryHeader,
  ContactCategoryConfig,
  ContactCategoryData,
  SupportedContactCategory,
  TaskTagCategory,
} from '@sib/utils';

interface UseContactCategoryOptionsProps {
  contactCategories: {
    vendorContact: ContactCategoryData;
    clientContact: ContactCategoryData;
    internalEmployees: ContactCategoryData;
  };
  categories?: SupportedContactCategory[];
  defaultContactsIds?: string[];
}

export const useContactCategoryOptions = ({
  contactCategories,
  categories = [
    TaskTagCategory.VendorContact,
    TaskTagCategory.ClientContact,
    TaskTagCategory.InternalEmployees,
  ],
  defaultContactsIds,
}: UseContactCategoryOptionsProps) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const sharedContactProps = {
      icon: Contact,
      color: 'var(--color-secondary)',
    };

    const categoryMap: Record<SupportedContactCategory, ContactCategoryConfig> =
      {
        [TaskTagCategory.VendorContact]: {
          label: t('components.form.contactField.contact.vendor'),
          ...contactCategories.vendorContact,
          field: TagType.Contact,
        },
        [TaskTagCategory.ClientContact]: {
          label: t('components.form.contactField.contact.client'),
          ...contactCategories.clientContact,
          field: TagType.Contact,
        },
        [TaskTagCategory.InternalEmployees]: {
          label: t('components.form.contactField.contact.internalEmployees'),
          ...contactCategories.internalEmployees,
          field: TagType.Employee,
        },
      };

    return categories.map(category => {
      const { label, options, sentryRef, hasNextPage, field, isLoading } =
        categoryMap[category];
      const filteredOptions = options.filter(
        option => !defaultContactsIds?.includes(option.value)
      );

      return {
        ...createCategoryHeader(
          category,
          label,
          sharedContactProps.icon,
          sharedContactProps.color,
          filteredOptions,
          field
        ),
        isLoading,
        totalItems: filteredOptions.length,
        sentryRef,
        hasNextPage,
      };
    });
  }, [
    contactCategories.vendorContact,
    contactCategories.clientContact,
    contactCategories.internalEmployees,
    categories,
    defaultContactsIds,
    t,
  ]);
};
